import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import LoginRequiredConfirm from '@/components/feature/LoginRequiredConfirm';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAdmin?: boolean;
}

export default function ProtectedRoute({ children, requireAdmin = false }: ProtectedRouteProps) {
  const { isLoggedIn, isAdmin, isReady } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showLoginConfirm, setShowLoginConfirm] = useState(false);

  useEffect(() => {
    if (!isReady) return;
    if (!isLoggedIn) {
      setShowLoginConfirm(true);
    } else {
      setShowLoginConfirm(false);
    }
  }, [isLoggedIn, isReady]);

  useEffect(() => {
    if (!isReady) return;
    if (isLoggedIn && requireAdmin && !isAdmin) {
      navigate('/', { replace: true });
    }
  }, [isLoggedIn, isAdmin, isReady, requireAdmin, navigate]);

  if (!isReady) {
    return (
      <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center px-6">
        <div className="w-full max-w-md bg-white border border-gray-100 rounded-2xl p-6 text-center">
          <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-[#0ea5e9]/10 flex items-center justify-center">
            <i className="ri-loader-4-line animate-spin text-[#0ea5e9] text-xl"></i>
          </div>
          <h2 className="text-base font-semibold text-gray-900 mb-2">正在同步登录状态</h2>
          <p className="text-sm text-gray-500">请稍候，系统正在检查当前标签页的会话。</p>
        </div>
      </div>
    );
  }

  if (!isLoggedIn) {
    return (
      <>
        <LoginRequiredConfirm
          open={showLoginConfirm}
          title="需要登录后访问"
          description="该页面需要登录后才能访问，是否前往登录页面？"
          onConfirm={() => {
            setShowLoginConfirm(false);
            navigate('/login', { replace: true, state: { from: location.pathname } });
          }}
          onCancel={() => setShowLoginConfirm(false)}
        />
        {!showLoginConfirm && (
          <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center px-6">
            <div className="w-full max-w-md bg-white border border-gray-100 rounded-2xl p-6 text-center">
              <h2 className="text-base font-semibold text-gray-900 mb-2">需要登录后访问</h2>
              <p className="text-sm text-gray-500 mb-4">当前页面仅限登录用户访问。</p>
              <button
                type="button"
                onClick={() => setShowLoginConfirm(true)}
                className="inline-flex items-center justify-center px-4 py-2 bg-[#0ea5e9] text-white text-sm rounded-lg hover:bg-[#0284c7] transition-colors cursor-pointer"
              >
                去登录
              </button>
            </div>
          </div>
        )}
      </>
    );
  }

  if (requireAdmin && !isAdmin) return null;

  return <>{children}</>;
}
